import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { compressImageDataUrl } from '../lib/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, CalendarCheck, Trophy, ListTodo, ClipboardList, Shield, ShieldAlert, ShieldCheck, FileText, Upload, Trash2, Download, User, UserX, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  if (status === 'green') return <Badge className="bg-emerald-100 text-emerald-700 text-xs">Eligible</Badge>;
  if (status === 'yellow') return <Badge className="bg-amber-100 text-amber-700 text-xs">Warning</Badge>;
  if (status === 'red') return <Badge className="bg-red-100 text-red-700 text-xs">Locked</Badge>;
  return <Badge variant="secondary" className="text-xs">N/A</Badge>;
};

const DOC_TYPES = ['Aadhar Card', 'PAN Card', 'Passport', 'Driving License', 'Voter ID', 'Educational Certificate', 'Experience Certificate', 'Offer Letter', 'Relieving Letter', 'Other'];

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-neutral-50">
      <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
      <span className="text-sm text-neutral-900 font-medium">{value || '-'}</span>
    </div>
  );
}

export default function EmployeeProfilePage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [exitDialog, setExitDialog] = useState(false);
  const [exitForm, setExitForm] = useState({ exit_date: new Date().toISOString().slice(0, 10), exit_reason: '' });
  const [docForm, setDocForm] = useState({ doc_type: '', doc_name: '', file_base64: '' });
  const [uploading, setUploading] = useState(false);

  const isAdmin = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);

  const fetchData = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get(`/employees/${employeeId}/profile`),
        api.get(`/employees/${employeeId}/documents`)
      ]);
      setProfile(pRes.data);
      setDocuments(dRes.data);
    } catch (err) {
      console.error('Profile fetch error:', err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (employeeId) fetchData();
  }, [employeeId]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (>25MB)'); return; }
    try {
      // Auto-compress images so users never need to shrink photos manually.
      // PDFs and other binaries are passed through unchanged.
      let dataUrl;
      if (file.type && file.type.startsWith('image/')) {
        dataUrl = await compressImageDataUrl(file);
      } else {
        dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = (ev) => res(ev.target.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
      }
      setDocForm(p => ({ ...p, file_base64: dataUrl, doc_name: p.doc_name || file.name }));
    } catch (err) {
      toast.error('Could not read file. Please try again.');
    }
  };

  const handleUploadDoc = async () => {
    if (!docForm.doc_type || !docForm.file_base64) { toast.error('Select document type and file'); return; }
    setUploading(true);
    try {
      await api.post(`/employees/${employeeId}/documents`, docForm);
      toast.success('Document uploaded');
      setUploadDialog(false);
      setDocForm({ doc_type: '', doc_name: '', file_base64: '' });
      const dRes = await api.get(`/employees/${employeeId}/documents`);
      setDocuments(dRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/employees/${employeeId}/documents/${docId}`);
      toast.success('Document deleted');
      setDocuments(d => d.filter(x => x.id !== docId));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDownloadDoc = async (docId, docName) => {
    try {
      const res = await api.get(`/employees/${employeeId}/documents/${docId}`);
      const data = res.data.file_base64;
      const a = document.createElement('a');
      a.href = data;
      a.download = docName;
      a.click();
    } catch (err) { toast.error('Failed to download'); }
  };

  const handleMarkExited = async () => {
    try {
      await api.put(`/employees/${employeeId}/exit`, exitForm);
      toast.success('Employee marked as exited');
      setExitDialog(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleReactivate = async () => {
    try {
      await api.put(`/employees/${employeeId}/reactivate`);
      toast.success('Employee reactivated');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-neutral-200 rounded animate-pulse" />)}</div>;
  if (!profile) return <p className="text-neutral-500">Employee not found</p>;

  const { employee: emp, branch_name, is_sales, eligibility } = profile;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-profile-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-sm" data-testid="profile-back-button">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">{emp.name}</h1>
            {is_sales && eligibility && <StatusBadge status={eligibility.status} />}
            <Badge className={`text-xs ${emp.emp_status === 'exited' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {emp.emp_status === 'exited' ? 'Exited' : 'Active'}
            </Badge>
          </div>
          <p className="text-neutral-500 text-sm mt-0.5">{emp.email} &middot; {emp.job_role} &middot; {branch_name}</p>
        </div>
        {isAdmin && emp.emp_status !== 'exited' && (
          <Button variant="outline" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" onClick={() => setExitDialog(true)}
            data-testid="mark-exited-button">
            <UserX className="h-4 w-4 mr-1.5" /> Mark as Exited
          </Button>
        )}
        {isAdmin && emp.emp_status === 'exited' && (
          <Button variant="outline" className="rounded-sm border-emerald-200 text-emerald-600 hover:bg-emerald-50" onClick={handleReactivate}
            data-testid="reactivate-button">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reactivate
          </Button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarCheck className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-[10px] text-neutral-500 uppercase font-bold tracking-wider">Attendance</p>
              <p className="text-xl font-heading font-black text-neutral-950">{profile.attendance_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-4 flex items-center gap-3">
            <Trophy className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-[10px] text-neutral-500 uppercase font-bold tracking-wider">Achievements</p>
              <p className="text-xl font-heading font-black text-neutral-950">{profile.achievements_approved}/{profile.achievements_total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-4 flex items-center gap-3">
            <ListTodo className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-[10px] text-neutral-500 uppercase font-bold tracking-wider">Tasks</p>
              <p className="text-xl font-heading font-black text-neutral-950">{profile.tasks_completed}/{profile.tasks_total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-[10px] text-neutral-500 uppercase font-bold tracking-wider">Documents</p>
              <p className="text-xl font-heading font-black text-neutral-950">{documents.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="details" className="rounded-sm" data-testid="tab-details">Personal Details</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-sm" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="achievements" className="rounded-sm" data-testid="tab-achievements">Achievements</TabsTrigger>
          <TabsTrigger value="tests" className="rounded-sm" data-testid="tab-tests">Tests</TabsTrigger>
        </TabsList>

        {/* Personal Details Tab */}
        <TabsContent value="details" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow label="Full Name" value={emp.name} />
                <InfoRow label="Email" value={emp.email} />
                <InfoRow label="Phone" value={emp.phone} />
                <InfoRow label="Gender" value={emp.gender} />
                <InfoRow label="Date of Birth" value={emp.dob} />
                <InfoRow label="Blood Group" value={emp.blood_group} />
                <InfoRow label="Address" value={emp.address} />
              </CardContent>
            </Card>
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Work Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow label="Employee Code" value={emp.employee_code} />
                <InfoRow label="Branch" value={branch_name} />
                <InfoRow label="Division" value={emp.division} />
                <InfoRow label="Job Role" value={emp.job_role} />
                <InfoRow label="Brand Classification" value={emp.brand_classification} />
                <InfoRow label="System Role" value={emp.system_role?.replace('_', ' ')} />
                <InfoRow label="Joining Date" value={emp.joining_date} />
                <InfoRow label="Status" value={emp.emp_status === 'exited' ? 'Exited' : 'Active'} />
                {emp.exit_date && <InfoRow label="Exit Date" value={emp.exit_date} />}
                {emp.exit_reason && <InfoRow label="Exit Reason" value={emp.exit_reason} />}
              </CardContent>
            </Card>
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">ID & Bank Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow label="Aadhar Number" value={emp.aadhar_number} />
                <InfoRow label="PAN Number" value={emp.pan_number} />
                <InfoRow label="UAN Number" value={emp.uan_number} />
                <InfoRow label="ESI Number" value={emp.esi_number} />
                <InfoRow label="Bank Account" value={emp.bank_account} />
                <InfoRow label="Bank Name" value={emp.bank_name} />
                <InfoRow label="IFSC Code" value={emp.ifsc_code} />
              </CardContent>
            </Card>
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Emergency & Education</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <InfoRow label="Emergency Contact" value={emp.emergency_contact} />
                <InfoRow label="Emergency Name" value={emp.emergency_name} />
                <InfoRow label="Education" value={emp.education} />
                <InfoRow label="Experience" value={emp.experience_years ? `${emp.experience_years} years` : null} />
                <InfoRow label="Previous Company" value={emp.previous_company} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-neutral-700">Uploaded Documents</CardTitle>
                {isAdmin && (
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setUploadDialog(true)}
                    data-testid="upload-doc-button">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Document
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {documents.length === 0 ? (
                <div className="text-center py-10 text-neutral-500">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
                  <p className="text-sm">No documents uploaded yet</p>
                  {isAdmin && <p className="text-xs text-neutral-400 mt-1">Upload ID proofs, certificates, and other documents</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between py-3 border-b border-neutral-50 last:border-0" data-testid={`doc-row-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-neutral-100 rounded-sm flex items-center justify-center">
                          <FileText className="h-4 w-4 text-neutral-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-800">{doc.doc_name}</p>
                          <p className="text-xs text-neutral-400">{doc.doc_type} &middot; {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadDoc(doc.id, doc.doc_name)}
                          data-testid={`download-doc-${doc.id}`}>
                          <Download className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteDoc(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Brand</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Target</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Achieved</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">%</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(profile.recent_achievements || []).map(a => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm">{a.date}</TableCell>
                      <TableCell className="text-sm font-medium">{a.brand_name}</TableCell>
                      <TableCell className="text-sm">{a.target?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm font-semibold">{a.achieved?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{a.target ? Math.round((a.achieved / a.target) * 100) : 0}%</TableCell>
                      <TableCell>
                        <Badge className={`text-xs capitalize ${a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!profile.recent_achievements || profile.recent_achievements.length === 0) && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-neutral-500">No records</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Tests Tab */}
        <TabsContent value="tests" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Test</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Score</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(profile.test_scores || []).map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="text-sm font-medium">{s.test_title}</TableCell>
                      <TableCell className="text-sm">{s.score}/{s.max_score}</TableCell>
                      <TableCell className="text-sm">{Math.round((s.score / s.max_score) * 100)}%</TableCell>
                    </TableRow>
                  ))}
                  {(!profile.test_scores || profile.test_scores.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="text-center py-8 text-neutral-500">No test scores</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md" data-testid="upload-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Upload Document</DialogTitle>
            <DialogDescription>Upload ID proofs, certificates, or other employee documents</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Document Type *</Label>
              <Select value={docForm.doc_type} onValueChange={v => setDocForm(p => ({ ...p, doc_type: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="doc-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Document Name</Label>
              <Input value={docForm.doc_name} onChange={e => setDocForm(p => ({ ...p, doc_name: e.target.value }))}
                className="rounded-sm" placeholder="e.g., Aadhar_front.pdf" data-testid="doc-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">File *</Label>
              <label className="block border-2 border-dashed border-neutral-300 rounded-sm p-6 text-center cursor-pointer hover:border-red-400 transition-colors">
                <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={handleFileSelect} className="hidden" data-testid="doc-file-input" />
                {docForm.file_base64 ? (
                  <p className="text-sm text-emerald-600 font-medium">File selected: {docForm.doc_name}</p>
                ) : (
                  <div className="text-neutral-400">
                    <Upload className="h-6 w-6 mx-auto mb-2" />
                    <p className="text-xs">Click to select file (PDF, Image, DOC - max 10MB)</p>
                  </div>
                )}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleUploadDoc} disabled={uploading} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="doc-upload-submit">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Employee Dialog */}
      <Dialog open={exitDialog} onOpenChange={setExitDialog}>
        <DialogContent className="max-w-sm" data-testid="exit-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-red-600">Mark Employee as Exited</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-neutral-600">This will mark <strong>{emp?.name}</strong> as exited. Their data and documents will be preserved in the Data Bank.</p>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Exit Date</Label>
              <Input type="date" value={exitForm.exit_date} onChange={e => setExitForm(p => ({ ...p, exit_date: e.target.value }))}
                className="rounded-sm" data-testid="exit-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Reason for Exit</Label>
              <Input value={exitForm.exit_reason} onChange={e => setExitForm(p => ({ ...p, exit_reason: e.target.value }))}
                className="rounded-sm" placeholder="e.g., Resigned, Terminated, Transfer..." data-testid="exit-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExitDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleMarkExited} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="confirm-exit-button">
              Confirm Exit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
